export const Types = {
	OPERATIONAL_DASHBOARD_INIT: "OPERATIONAL_DASHBOARD_INIT",
	AGENDA_INTEREST_REQUEST: "AGENDA_INTEREST_REQUEST",
	AGENDA_INTEREST_SUCCESS: "AGENDA_INTEREST_SUCCESS",
	AGENDA_INTEREST_FAILURE: "AGENDA_INTEREST_FAILURE",
	AGENDA_INTEREST_VERIFY_REQUEST: "AGENDA_INTEREST_VERIFY_REQUEST",
	AGENDA_INTEREST_VERIFY_SUCCESS: "AGENDA_INTEREST_VERIFY_SUCCESS",
	AGENDA_INTEREST_VERIFY_FAILURE: "AGENDA_INTEREST_VERIFY_FAILURE",
	OPERATIONAL_RESUMO_REQUEST: "OPERATIONAL_RESUMO_REQUEST",
	OPERATIONAL_RESUMO_SUCCESS: "OPERATIONAL_RESUMO_SUCCESS",
	OPERATIONAL_RESUMO_FAILURE: "OPERATIONAL_RESUMO_FAILURE",
	OPERATIONAL_INDICATOR_REQUEST: "OPERATIONAL_INDICATOR_REQUEST",
	OPERATIONAL_INDICATOR_SUCCESS: "OPERATIONAL_INDICATOR_SUCCESS",
	OPERATIONAL_INDICATOR_FAILURE: "OPERATIONAL_INDICATOR_FAILURE",
	PATIENT_CONTACT_UPDATE_REQUEST: "PATIENT_CONTACT_UPDATE_REQUEST",
	PATIENT_CONTACT_UPDATE_SUCCESS: "PATIENT_CONTACT_UPDATE_SUCCESS",
	PATIENT_CONTACT_UPDATE_FAILURE: "PATIENT_CONTACT_UPDATE_FAILURE",
	PATIENT_WHATSAPP_CONTACT_REGISTER_SUCCESS:
		"PATIENT_WHATSAPP_CONTACT_REGISTER_SUCCESS",
	TODAY_FOCUS_REQUEST: "TODAY_FOCUS_REQUEST",
	TODAY_FOCUS_SUCCESS: "TODAY_FOCUS_SUCCESS",
	TODAY_FOCUS_FAILURE: "TODAY_FOCUS_FAILURE",
	TODAY_FOCUS_INVALIDATE: "TODAY_FOCUS_INVALIDATE",
	TODAY_FOCUS_ACTION_REQUEST: "TODAY_FOCUS_ACTION_REQUEST",
	TODAY_FOCUS_ACTION_CONFIRMED: "TODAY_FOCUS_ACTION_CONFIRMED",
	TODAY_FOCUS_ACTION_SUCCESS: "TODAY_FOCUS_ACTION_SUCCESS",
	TODAY_FOCUS_ACTION_FAILURE: "TODAY_FOCUS_ACTION_FAILURE",
};

export type OperationalContactOrigin = "REEVALUATION" | "CRITICAL_ALERT";

export interface OperationalLastContact {
	ID_CONTATO: number;
	ID_AVALIADOR: number;
	AVALIADOR_NOME: string | null;
	CANAL: "WHATSAPP";
	EVENTO: "CONTACT_STARTED";
	ORIGEM: OperationalContactOrigin;
	DATA_HORA: string;
}

export interface OperationalPatient {
	ID_USUARIO: number;
	USUARIO_NOME?: string;
	USUARIO_FOTO?: string | null;
	USUARIO_CONTATO?: string;
	ULTIMA_AVALIACAO?: {
		DATA_HORA?: string;
		TIPO?: string;
		MOVIMENTO?: string;
	};
	AGENDAMENTO?: string;
	AGENDADO?: "S" | "N";
	ULTIMO_CONTATO: OperationalLastContact | null;
	[key: string]: any;
}

export type TodayFocusType =
	| "SHARE_REPORT"
	| "INVITE_REEVALUATION"
	| "REENGAGE_PATIENT";
export type TodayFocusEvent = "REPORT_SHARE_STARTED" | "DISMISSED";
export type TodayFocusActionKind = TodayFocusEvent | "CONTACT_STARTED";
export type TodayFocusQueueKey =
	| "compartilharRelatorio"
	| "convidarReavaliacao"
	| "resgatarCliente";

export interface TodayFocusItem extends OperationalPatient {
	FOCO_TIPO: TodayFocusType;
	ID_EXECUCAO_REFERENCIA: number;
}

export interface TodayFocusQueue {
	items: TodayFocusItem[];
	temProxima: boolean;
}

export interface TodayFocusQueues {
	compartilharRelatorio: TodayFocusQueue;
	convidarReavaliacao: TodayFocusQueue;
	resgatarCliente: TodayFocusQueue;
}

export interface TodayFocusActionState {
	loading: boolean;
	completion: "success" | "dismissed" | null;
	error: string | null;
	idempotencyKey: string | null;
	itemIdentity: string | null;
	actionKind: TodayFocusActionKind | null;
}

export interface TodayFocusState {
	queues: TodayFocusQueues;
	loading: boolean;
	loaded: boolean;
	error: string | null;
	limitPerType: number;
	generatedAt: string | null;
	actions: Record<TodayFocusQueueKey, TodayFocusActionState>;
}

export interface TodayFocusActionRequest {
	queueKey: TodayFocusQueueKey;
	item: TodayFocusItem;
	actionKind: TodayFocusActionKind;
	idempotencyKey: string;
	onSuccess?: (result?: any) => void;
	onFailure?: () => void;
}

export type OperationalIndicator =
	| "PRONTOS_REAVALIACAO"
	| "PACIENTES_INATIVOS"
	| "PACIENTES_AVALIADOS";

export interface OperationalPagination {
	pagina: number;
	limite: number;
	total: number;
	totalPaginas: number | null;
	temProxima: boolean;
}

export interface OperationalIndicatorData {
	total: number;
	items: OperationalPatient[];
	paginacao: OperationalPagination | null;
	loading: boolean;
	error: string | null;
}

export interface OperationalLatestEvaluation {
	id: number;
	realizadaEm: string;
	paciente: { id: number; nome: string; foto: string | null };
	profissional: { id: number; nome: string } | null;
	atividade: {
		tipo: "PROTOCOLO" | "EXERCICIO";
		id: number;
		titulo: string;
	} | null;
}

export interface OperationalIndicatorRequest {
	indicator: OperationalIndicator;
	page?: number;
	limit?: number;
	search?: string;
	append?: boolean;
}

export interface PatientContactUpdateRequest {
	patientId: number | string;
	phone: string;
	onSuccess?: () => void;
	onFailure?: () => void;
}

export interface OperationalDashboardState {
	agendaInterestRequested: boolean;
	loadingAgendaInterest: boolean;
	agendaInterestError: string | null;
	prontosReavaliacao: OperationalIndicatorData;
	pacientesInativos: OperationalIndicatorData;
	pacientesAvaliados: OperationalIndicatorData;
	loadingResumo: boolean;
	hasLoadedResumo: boolean;
	resumoError: string | null;
	ultimaAvaliacaoFinalizada: OperationalLatestEvaluation | null;
	geradoEm: string | null;
	updatingContactPatientIds: Record<string, boolean>;
	contactUpdateErrors: Record<string, string | null>;
	todayFocus: TodayFocusState;
}

const emptyIndicator = (): OperationalIndicatorData => ({
	total: 0,
	items: [],
	paginacao: null,
	loading: false,
	error: null,
});

const emptyTodayFocusQueue = (): TodayFocusQueue => ({
	items: [],
	temProxima: false,
});

const emptyTodayFocusAction = (): TodayFocusActionState => ({
	loading: false,
	completion: null,
	error: null,
	idempotencyKey: null,
	itemIdentity: null,
	actionKind: null,
});

const emptyTodayFocus = (): TodayFocusState => ({
	queues: {
		compartilharRelatorio: emptyTodayFocusQueue(),
		convidarReavaliacao: emptyTodayFocusQueue(),
		resgatarCliente: emptyTodayFocusQueue(),
	},
	loading: false,
	loaded: false,
	error: null,
	limitPerType: 20,
	generatedAt: null,
	actions: {
		compartilharRelatorio: emptyTodayFocusAction(),
		convidarReavaliacao: emptyTodayFocusAction(),
		resgatarCliente: emptyTodayFocusAction(),
	},
});

const INITIAL_STATE: OperationalDashboardState = {
	agendaInterestRequested: false,
	loadingAgendaInterest: false,
	agendaInterestError: null,
	prontosReavaliacao: emptyIndicator(),
	pacientesInativos: emptyIndicator(),
	pacientesAvaliados: emptyIndicator(),
	loadingResumo: true,
	hasLoadedResumo: false,
	resumoError: null,
	ultimaAvaliacaoFinalizada: null,
	geradoEm: null,
	updatingContactPatientIds: {},
	contactUpdateErrors: {},
	todayFocus: emptyTodayFocus(),
};

const focusItemIdentity = (item?: TodayFocusItem | null) =>
	item
		? `${item.FOCO_TIPO}:${item.ID_USUARIO}:${item.ID_EXECUCAO_REFERENCIA}`
		: null;

const normalizeTodayFocusQueue = (value: any): TodayFocusQueue => ({
	items: Array.isArray(value?.items) ? value.items : [],
	temProxima: value?.temProxima === true,
});

const reconcileTodayFocusAction = (
	actionState: TodayFocusActionState,
	queue: TodayFocusQueue
) =>
	actionState.itemIdentity &&
	actionState.itemIdentity === focusItemIdentity(queue.items[0])
		? actionState
		: emptyTodayFocusAction();

type IndicatorStateKey =
	| "prontosReavaliacao"
	| "pacientesInativos"
	| "pacientesAvaliados";

const indicatorStateKey: Record<OperationalIndicator, IndicatorStateKey> = {
	PRONTOS_REAVALIACAO: "prontosReavaliacao",
	PACIENTES_INATIVOS: "pacientesInativos",
	PACIENTES_AVALIADOS: "pacientesAvaliados",
};

const normalizeIndicator = (value: any): OperationalIndicatorData => ({
	total: Number(value?.total) || 0,
	items: Array.isArray(value?.items)
		? value.items.map((patient: any) => ({
				...patient,
				ULTIMO_CONTATO: patient?.ULTIMO_CONTATO ?? null,
		  }))
		: [],
	paginacao: value?.paginacao || null,
	loading: false,
	error: null,
});

export default function operationalDashboard(
	state: OperationalDashboardState = INITIAL_STATE,
	action: any
): OperationalDashboardState {
	switch (action.type) {
		case Types.OPERATIONAL_DASHBOARD_INIT:
			return { ...state };
		case Types.AGENDA_INTEREST_REQUEST:
			return {
				...state,
				loadingAgendaInterest: true,
				agendaInterestError: null,
			};
		case Types.AGENDA_INTEREST_SUCCESS:
			return {
				...state,
				loadingAgendaInterest: false,
				agendaInterestRequested: true,
			};
		case Types.AGENDA_INTEREST_FAILURE:
			return {
				...state,
				loadingAgendaInterest: false,
				agendaInterestError: action.payload,
			};
		case Types.AGENDA_INTEREST_VERIFY_REQUEST:
		case Types.AGENDA_INTEREST_VERIFY_FAILURE:
			return state;
		case Types.AGENDA_INTEREST_VERIFY_SUCCESS:
			return { ...state, agendaInterestRequested: action.payload };
		case Types.OPERATIONAL_RESUMO_REQUEST:
			return { ...state, loadingResumo: true, resumoError: null };
		case Types.OPERATIONAL_RESUMO_SUCCESS:
			return {
				...state,
				loadingResumo: false,
				hasLoadedResumo: true,
				prontosReavaliacao: normalizeIndicator(
					action.payload?.prontosReavaliacao
				),
				pacientesInativos: normalizeIndicator(
					action.payload?.pacientesInativos
				),
				pacientesAvaliados: normalizeIndicator(
					action.payload?.pacientesAvaliados
				),
				ultimaAvaliacaoFinalizada:
					action.payload?.ultimaAvaliacaoFinalizada ?? null,
				geradoEm: action.payload?.geradoEm ?? null,
			};
		case Types.OPERATIONAL_RESUMO_FAILURE:
			return { ...state, loadingResumo: false, resumoError: action.payload };
		case Types.OPERATIONAL_INDICATOR_REQUEST: {
			const key =
				indicatorStateKey[action.payload.indicator as OperationalIndicator];
			if (!key) return state;
			return {
				...state,
				[key]: { ...state[key], loading: true, error: null },
			};
		}
		case Types.OPERATIONAL_INDICATOR_SUCCESS: {
			const { indicator, data, append } = action.payload;
			const key = indicatorStateKey[indicator as OperationalIndicator];
			if (!key) return state;
			const current = state[key];
			const incomingItems: OperationalPatient[] = Array.isArray(data?.items)
				? data.items.map((patient: any) => ({
						...patient,
						ULTIMO_CONTATO: patient?.ULTIMO_CONTATO ?? null,
				  }))
				: [];
			const items = append
				? Array.from(
						new Map(
							[...current.items, ...incomingItems].map((patient) => [
								String(patient.ID_USUARIO),
								patient,
							])
						).values()
				  )
				: incomingItems;
			return {
				...state,
				[key]: {
					total: Number(data?.total) || 0,
					items,
					paginacao: data?.paginacao || null,
					loading: false,
					error: null,
				},
			};
		}
		case Types.OPERATIONAL_INDICATOR_FAILURE: {
			const { indicator, error } = action.payload;
			const key = indicatorStateKey[indicator as OperationalIndicator];
			if (!key) return state;
			return {
				...state,
				[key]: { ...state[key], loading: false, error },
			};
		}
		case Types.PATIENT_CONTACT_UPDATE_REQUEST: {
			const patientId = String(action.payload.patientId);
			return {
				...state,
				updatingContactPatientIds: {
					...state.updatingContactPatientIds,
					[patientId]: true,
				},
				contactUpdateErrors: {
					...state.contactUpdateErrors,
					[patientId]: null,
				},
			};
		}
		case Types.PATIENT_CONTACT_UPDATE_SUCCESS: {
			const patientId = String(action.payload.patientId);
			const updateItems = (indicator: OperationalIndicatorData) => ({
				...indicator,
				items: indicator.items.map((patient) =>
					String(patient?.ID_USUARIO) === patientId
						? { ...patient, USUARIO_CONTATO: action.payload.phone }
						: patient
				),
			});
			return {
				...state,
				prontosReavaliacao: updateItems(state.prontosReavaliacao),
				pacientesInativos: updateItems(state.pacientesInativos),
				updatingContactPatientIds: {
					...state.updatingContactPatientIds,
					[patientId]: false,
				},
				contactUpdateErrors: {
					...state.contactUpdateErrors,
					[patientId]: null,
				},
			};
		}
		case Types.PATIENT_CONTACT_UPDATE_FAILURE: {
			const patientId = String(action.payload.patientId);
			return {
				...state,
				updatingContactPatientIds: {
					...state.updatingContactPatientIds,
					[patientId]: false,
				},
				contactUpdateErrors: {
					...state.contactUpdateErrors,
					[patientId]: action.payload.error,
				},
			};
		}
		case Types.PATIENT_WHATSAPP_CONTACT_REGISTER_SUCCESS: {
			const patientId = String(action.payload.patientId);
			const updateItems = (indicator: OperationalIndicatorData) => ({
				...indicator,
				items: indicator.items.map((patient) =>
					String(patient.ID_USUARIO) === patientId
						? { ...patient, ULTIMO_CONTATO: action.payload.lastContact }
						: patient
				),
			});
			return {
				...state,
				prontosReavaliacao: updateItems(state.prontosReavaliacao),
				pacientesInativos: updateItems(state.pacientesInativos),
				pacientesAvaliados: updateItems(state.pacientesAvaliados),
			};
		}
		case Types.TODAY_FOCUS_REQUEST:
			return {
				...state,
				todayFocus: { ...state.todayFocus, loading: true, error: null },
			};
		case Types.TODAY_FOCUS_SUCCESS: {
			const queues = {
				compartilharRelatorio: normalizeTodayFocusQueue(
					action.payload?.focoHoje?.compartilharRelatorio
				),
				convidarReavaliacao: normalizeTodayFocusQueue(
					action.payload?.focoHoje?.convidarReavaliacao
				),
				resgatarCliente: normalizeTodayFocusQueue(
					action.payload?.focoHoje?.resgatarCliente
				),
			};
			return {
				...state,
				todayFocus: {
					...state.todayFocus,
					queues,
					actions: {
						compartilharRelatorio: reconcileTodayFocusAction(
							state.todayFocus.actions.compartilharRelatorio,
							queues.compartilharRelatorio
						),
						convidarReavaliacao: reconcileTodayFocusAction(
							state.todayFocus.actions.convidarReavaliacao,
							queues.convidarReavaliacao
						),
						resgatarCliente: reconcileTodayFocusAction(
							state.todayFocus.actions.resgatarCliente,
							queues.resgatarCliente
						),
					},
					loading: false,
					loaded: true,
					error: null,
					limitPerType:
						Number(action.payload?.limitePorTipo) ||
						state.todayFocus.limitPerType,
					generatedAt: action.payload?.geradoEm || null,
				},
			};
		}
		case Types.TODAY_FOCUS_FAILURE:
			return {
				...state,
				todayFocus: {
					...state.todayFocus,
					loading: false,
					error: action.payload,
				},
			};
		case Types.TODAY_FOCUS_INVALIDATE:
			return { ...state, todayFocus: emptyTodayFocus() };
		case Types.TODAY_FOCUS_ACTION_REQUEST: {
			const request = action.payload as TodayFocusActionRequest;
			return {
				...state,
				todayFocus: {
					...state.todayFocus,
					actions: {
						...state.todayFocus.actions,
						[request.queueKey]: {
							loading: true,
							completion: null,
							error: null,
							idempotencyKey: request.idempotencyKey,
							itemIdentity: focusItemIdentity(request.item),
							actionKind: request.actionKind,
						},
					},
				},
			};
		}
		case Types.TODAY_FOCUS_ACTION_CONFIRMED: {
			const { queueKey, itemIdentity, completion } = action.payload;
			const typedQueueKey = queueKey as TodayFocusQueueKey;
			const queue = state.todayFocus.queues[typedQueueKey];
			if (!queue || focusItemIdentity(queue.items[0]) !== itemIdentity) {
				return state;
			}
			return {
				...state,
				todayFocus: {
					...state.todayFocus,
					actions: {
						...state.todayFocus.actions,
						[typedQueueKey]: {
							...state.todayFocus.actions[typedQueueKey],
							loading: false,
							completion,
						},
					},
				},
			};
		}
		case Types.TODAY_FOCUS_ACTION_SUCCESS: {
			const { queueKey, itemIdentity } = action.payload;
			const typedQueueKey = queueKey as TodayFocusQueueKey;
			const queue = state.todayFocus.queues[typedQueueKey];
			if (!queue || focusItemIdentity(queue.items[0]) !== itemIdentity) {
				if (
					state.todayFocus.actions[typedQueueKey]?.itemIdentity !== itemIdentity
				) {
					return state;
				}
				return {
					...state,
					todayFocus: {
						...state.todayFocus,
						actions: {
							...state.todayFocus.actions,
							[queueKey]: emptyTodayFocusAction(),
						},
					},
				};
			}
			return {
				...state,
				todayFocus: {
					...state.todayFocus,
					queues: {
						...state.todayFocus.queues,
						[queueKey]: { ...queue, items: queue.items.slice(1) },
					},
					actions: {
						...state.todayFocus.actions,
						[queueKey]: emptyTodayFocusAction(),
					},
				},
			};
		}
		case Types.TODAY_FOCUS_ACTION_FAILURE: {
			const { queueKey, itemIdentity, actionKind, idempotencyKey, error } =
				action.payload;
			const typedQueueKey = queueKey as TodayFocusQueueKey;
			const queue = state.todayFocus.queues[typedQueueKey];
			if (!queue || focusItemIdentity(queue.items[0]) !== itemIdentity) {
				if (
					state.todayFocus.actions[typedQueueKey]?.itemIdentity !== itemIdentity
				) {
					return state;
				}
				return {
					...state,
					todayFocus: {
						...state.todayFocus,
						actions: {
							...state.todayFocus.actions,
							[queueKey]: emptyTodayFocusAction(),
						},
					},
				};
			}
			return {
				...state,
				todayFocus: {
					...state.todayFocus,
					actions: {
						...state.todayFocus.actions,
						[queueKey]: {
							loading: false,
							completion: null,
							error,
							idempotencyKey,
							itemIdentity,
							actionKind,
						},
					},
				},
			};
		}
		default:
			return state;
	}
}

export const OperationalDashboardActions = {
	init_dashboard: () => ({ type: Types.OPERATIONAL_DASHBOARD_INIT }),
	agenda_interest_request: () => ({ type: Types.AGENDA_INTEREST_REQUEST }),
	agenda_interest_success: () => ({ type: Types.AGENDA_INTEREST_SUCCESS }),
	agenda_interest_failure: (error: string) => ({
		type: Types.AGENDA_INTEREST_FAILURE,
		payload: error,
	}),
	agenda_interest_verify_request: () => ({
		type: Types.AGENDA_INTEREST_VERIFY_REQUEST,
	}),
	agenda_interest_verify_success: (requested: boolean) => ({
		type: Types.AGENDA_INTEREST_VERIFY_SUCCESS,
		payload: requested,
	}),
	agenda_interest_verify_failure: (error: string) => ({
		type: Types.AGENDA_INTEREST_VERIFY_FAILURE,
		payload: error,
	}),
	operational_resumo_request: () => ({
		type: Types.OPERATIONAL_RESUMO_REQUEST,
	}),
	operational_resumo_success: (data: any) => ({
		type: Types.OPERATIONAL_RESUMO_SUCCESS,
		payload: data,
	}),
	operational_resumo_failure: (error: string) => ({
		type: Types.OPERATIONAL_RESUMO_FAILURE,
		payload: error,
	}),
	operational_indicator_request: (request: OperationalIndicatorRequest) => ({
		type: Types.OPERATIONAL_INDICATOR_REQUEST,
		payload: request,
	}),
	operational_indicator_success: (
		indicator: OperationalIndicator,
		data: any,
		append = false
	) => ({
		type: Types.OPERATIONAL_INDICATOR_SUCCESS,
		payload: { indicator, data, append },
	}),
	operational_indicator_failure: (
		indicator: OperationalIndicator,
		error: string
	) => ({
		type: Types.OPERATIONAL_INDICATOR_FAILURE,
		payload: { indicator, error },
	}),
	patient_contact_update_request: (request: PatientContactUpdateRequest) => ({
		type: Types.PATIENT_CONTACT_UPDATE_REQUEST,
		payload: request,
	}),
	patient_contact_update_success: (
		patientId: number | string,
		phone: string
	) => ({
		type: Types.PATIENT_CONTACT_UPDATE_SUCCESS,
		payload: { patientId, phone },
	}),
	patient_contact_update_failure: (
		patientId: number | string,
		error: string
	) => ({
		type: Types.PATIENT_CONTACT_UPDATE_FAILURE,
		payload: { patientId, error },
	}),
	patient_whatsapp_contact_register_success: (
		patientId: number | string,
		lastContact: OperationalLastContact
	) => ({
		type: Types.PATIENT_WHATSAPP_CONTACT_REGISTER_SUCCESS,
		payload: { patientId, lastContact },
	}),
	today_focus_request: () => ({ type: Types.TODAY_FOCUS_REQUEST }),
	today_focus_success: (data: any) => ({
		type: Types.TODAY_FOCUS_SUCCESS,
		payload: data,
	}),
	today_focus_failure: (error: string) => ({
		type: Types.TODAY_FOCUS_FAILURE,
		payload: error,
	}),
	today_focus_invalidate: () => ({ type: Types.TODAY_FOCUS_INVALIDATE }),
	today_focus_action_request: (request: TodayFocusActionRequest) => ({
		type: Types.TODAY_FOCUS_ACTION_REQUEST,
		payload: request,
	}),
	today_focus_action_confirmed: (
		queueKey: TodayFocusQueueKey,
		itemIdentity: string,
		completion: "success" | "dismissed"
	) => ({
		type: Types.TODAY_FOCUS_ACTION_CONFIRMED,
		payload: { queueKey, itemIdentity, completion },
	}),
	today_focus_action_success: (
		queueKey: TodayFocusQueueKey,
		itemIdentity: string
	) => ({
		type: Types.TODAY_FOCUS_ACTION_SUCCESS,
		payload: { queueKey, itemIdentity },
	}),
	today_focus_action_failure: (
		request: TodayFocusActionRequest,
		itemIdentity: string,
		error: string
	) => ({
		type: Types.TODAY_FOCUS_ACTION_FAILURE,
		payload: {
			queueKey: request.queueKey,
			itemIdentity,
			actionKind: request.actionKind,
			idempotencyKey: request.idempotencyKey,
			error,
		},
	}),
};
